import type { UsageMonthView } from "@starsach/shared-types";
import { useUsage } from "../api/hooks";
import { useFormat } from "../lib/useFormat";
import { Hint } from "./Hint";
import { Card, ErrorState, StatusPill } from "./ui";

/**
 * #272: what this environment's stack metered - Connect's own billed
 * usage, by month - and the minute-equivalents the price rule counts.
 * The amount is the organisation's (its live environments together), on
 * the platform view; here the customer sees the numbers before the
 * invoice does, with where they came from.
 */
export function UsageCard({ environmentId }: { environmentId: string }) {
  const usage = useUsage(environmentId);
  const fmt = useFormat();
  const months = usage.data?.months ?? [];
  const n = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return (
    <Card title="Usage">
      {usage.isError && <ErrorState error={usage.error} />}
      {usage.data && months.length === 0 && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400">No usage reported yet. The stack reads its Amazon Connect usage once a day; the first figures arrive the morning after it is deployed.</p>
      )}
      {months.length > 0 && (
        <table className="w-full text-sm" aria-label="Usage by month">
          <thead>
            <tr className="text-left text-xs text-neutral-500">
              <th scope="col" className="py-1 pr-3 font-medium">Month</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Voice min</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Chats</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Emails</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Cases</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Min-eq <Hint id="usage.minute-equivalents" label="what a minute-equivalent is" /></th>
              <th scope="col" className="py-1 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m: UsageMonthView) => (
              <tr key={m.month} className="border-t border-neutral-100 dark:border-neutral-800">
                <td className="py-1.5 pr-3 font-medium">{m.month}{m.complete ? "" : <span className="ml-1 text-xs font-normal text-neutral-500">so far</span>}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{n(m.voice_minutes)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{n(m.chat_messages)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{n(m.emails)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{n(m.cases)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums font-medium">{n(m.minute_equivalents)}</td>
                <td className="py-1.5">
                  <StatusPill status={m.source === "cost-explorer" ? "your bill" : "approximate"} tone={m.source === "cost-explorer" ? "ok" : "warning"} />
                  <span className="ml-1 text-xs text-neutral-500">as of {fmt.time(m.as_of)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {months.some((m) => m.source === "connect-metrics") && (
        <p className="mt-2 text-xs text-neutral-500">Approximate: this account could not read its Cost Explorer, so the figures come from Connect's own metrics - contact time rather than billed minutes, and chat as minutes rather than messages. Ask your AWS payer to enable Cost Explorer access for the account to get the exact lines.</p>
      )}
    </Card>
  );
}
